import { useCallback, useEffect, useState } from 'react';
import {
  Activity,
  BadgeCheck,
  CalendarPlus,
  CheckCircle2,
  Clock,
  Download,
  FileSearch,
  Gauge,
  Layers,
  Lock,
  Radar,
  ReceiptText,
  UserCheck,
  Users,
  Wallet,
  XCircle,
} from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { FinanceiroAdmin, PanoramaCapturaAdmin, ResumoAdmin } from '../../api/tipos';
import {
  Aviso,
  Esqueleto,
  Indicador,
  Indicadores,
  Progresso,
  Selecao,
  TituloSecao,
} from '../../componentes/ui';
import { Pagina, TituloPagina } from '../../componentes/Pagina';
import { numero, plural, reais } from '../../util/formato';
import css from './VisaoGeralAdmin.module.css';
/** Períodos de consulta, no mesmo espírito do seletor do Asaas. */
const PERIODOS = [
  { chave: 'mes', rotulo: 'Este mês' },
  { chave: 'ultimos30', rotulo: 'Últimos 30 dias' },
  { chave: 'ano', rotulo: 'Este ano' },
  { chave: 'tudo', rotulo: 'Tudo' },
] as const;

type ChavePeriodo = (typeof PERIODOS)[number]['chave'];

/** Atualização do card de operação, para ver a fila andar sem recarregar. */
const INTERVALO_FILA_MS = 10_000;

/** "2 processando · 1 aguardando · 1.200 de 5.000 chaves (24%)". */
function notaAndamento(op: ResumoAdmin['operacao']): string {
  const partes = [
    `${numero(op.lotesProcessando)} processando`,
    `${numero(op.lotesPendentes)} aguardando`,
  ];
  const { processadas, total } = op.chavesEmAndamento;
  if (total > 0) {
    const pct = Math.floor((processadas / total) * 100);
    partes.push(`${numero(processadas)} de ${numero(total)} chaves (${pct}%)`);
  }
  return partes.join(' · ');
}

/** Converte o período escolhido em datas `AAAA-MM-DD` para a API. */
function intervalo(p: ChavePeriodo): { dataInicio?: string; dataFim?: string } {
  const hoje = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  switch (p) {
    case 'mes':
      return {
        dataInicio: iso(
          new Date(Date.UTC(hoje.getUTCFullYear(), hoje.getUTCMonth(), 1)),
        ),
        dataFim: iso(hoje),
      };
    case 'ultimos30':
      return {
        dataInicio: iso(new Date(Date.now() - 30 * 86_400_000)),
        dataFim: iso(hoje),
      };
    case 'ano':
      return {
        dataInicio: iso(new Date(Date.UTC(hoje.getUTCFullYear(), 0, 1))),
        dataFim: iso(hoje),
      };
    case 'tudo':
      return {};
  }
}


export function VisaoGeralAdmin() {
  const [periodo, setPeriodo] = useState<ChavePeriodo>('mes');
  const [resumo, setResumo] = useState<ResumoAdmin | null>(null);
  const [financeiro, setFinanceiro] = useState<FinanceiroAdmin | null>(null);
  const [captura, setCaptura] = useState<PanoramaCapturaAdmin | null | 'erro'>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    const { dataInicio, dataFim } = intervalo(periodo);
    const q = new URLSearchParams();
    if (dataInicio) q.set('dataInicio', dataInicio);
    if (dataFim) q.set('dataFim', dataFim);

    try {
      const [r, f] = await Promise.all([
        api<ResumoAdmin>('/admin/resumo'),
        api<FinanceiroAdmin>(`/admin/financeiro?${q.toString()}`),
      ]);
      setResumo(r);
      setFinanceiro(f);
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Falha ao carregar o painel.',
      );
    }
  }, [periodo]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Só o resumo é atualizado sozinho: é dele que sai o estado da fila. O
  // financeiro consulta o Asaas a cada chamada e fica no carregamento manual.
  useEffect(() => {
    const t = setInterval(() => {
      api<ResumoAdmin>('/admin/resumo')
        .then(setResumo)
        .catch(() => {
          // Falha pontual: mantém o último resumo em vez de piscar erro.
        });
    }, INTERVALO_FILA_MS);
    return () => clearInterval(t);
  }, []);

  // Atalho para a captura automática: um número só, carregado uma vez. O
  // panorama completo (e a atualização contínua) mora em /admin/captura.
  useEffect(() => {
    api<PanoramaCapturaAdmin>('/admin/captura')
      .then(setCaptura)
      .catch(() => setCaptura('erro'));
  }, []);

  return (
    <Pagina>
      <TituloPagina
        titulo="Visão geral"
        descricao="O negócio num relance: contas, créditos em circulação e o que já entrou no caixa."
        acoes={
          <Selecao
            rotulo="Período"
            rotuloOculto
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value as ChavePeriodo)}
          >
            {PERIODOS.map((p) => (
              <option key={p.chave} value={p.chave}>
                {p.rotulo}
              </option>
            ))}
          </Selecao>
        }
      />

      {erro && <Aviso>{erro}</Aviso>}

      <Numeros resumo={resumo} financeiro={financeiro} captura={captura} />
    </Pagina>
  );
}

/* ── Visão geral ────────────────────────────────────────────────────────── */

function Numeros({
  resumo,
  financeiro,
  captura,
}: {
  resumo: ResumoAdmin | null;
  financeiro: FinanceiroAdmin | null;
  captura: PanoramaCapturaAdmin | null | 'erro';
}) {
  if (!resumo) {
    return (
      <div className={css.secao}>
        <Esqueleto altura={20} largura={200} />
        <div className={css.esqueletos}>
          {[0, 1, 2, 3].map((i) => (
            <Esqueleto key={i} altura={112} />
          ))}
        </div>
      </div>
    );
  }

  const op = resumo.operacao;

  return (
    <>
      <SituacaoCobrancas financeiro={financeiro} />

      <section className={css.secao}>
        <TituloSecao descricao="Dinheiro que já entrou e ainda vira serviço.">
          Créditos em circulação
        </TituloSecao>
        <Indicadores>
          <Indicador
            rotulo="Saldo nas contas"
            icone={<Wallet />}
            valor={reais(resumo.creditos.saldoEmContas)}
            detalhe="Passivo: já recebido, ainda não consumido"
            tom="alerta"
          />
          <Indicador
            rotulo="Reservado agora"
            icone={<Lock />}
            valor={reais(resumo.creditos.reservado)}
            detalhe={`${plural(resumo.creditos.reservasAtivas, 'busca travando', 'buscas travando')} saldo`}
          />
          <Indicador
            rotulo="Consumido no mês"
            icone={<Activity />}
            valor={reais(resumo.creditos.consumoMes)}
            detalhe={`${reais(resumo.creditos.consumoTotal)} desde o início`}
            tom="ok"
          />
          <Indicador
            rotulo="Recargas pagas"
            icone={<ReceiptText />}
            valor={reais(resumo.creditos.recebidoTotal)}
            detalhe={plural(resumo.creditos.recargasPagas, 'recarga')}
          />
        </Indicadores>
      </section>

      <section className={css.secao}>
        <TituloSecao>Contas</TituloSecao>
        <Indicadores>
          <Indicador
            rotulo="Total"
            icone={<Users />}
            valor={numero(resumo.usuarios.total)}
            para="/admin/clientes"
          />
          <Indicador
            rotulo="Verificadas"
            icone={<BadgeCheck />}
            valor={numero(resumo.usuarios.verificados)}
            detalhe={`${plural(resumo.usuarios.naoVerificados, 'pendente')}`}
          />
          <Indicador
            rotulo="Novas no mês"
            icone={<CalendarPlus />}
            valor={numero(resumo.usuarios.novosNoMes)}
          />
          <Indicador
            rotulo="Ativas (7 dias)"
            icone={<UserCheck />}
            valor={numero(resumo.usuarios.ativosSemana)}
            detalhe="Movimentaram crédito"
          />
        </Indicadores>
        {resumo.usuarios.inativos > 0 && (
          <Aviso tom="alerta">
            {plural(resumo.usuarios.inativos, 'conta desativada', 'contas desativadas')}.
          </Aviso>
        )}
      </section>

      <section className={css.secao}>
        <TituloSecao descricao="Atualiza sozinho a cada 10 segundos.">Operação</TituloSecao>
        <Indicadores>
          <Indicador
            rotulo="Buscas em andamento"
            icone={<Layers />}
            valor={numero(op.lotesProcessando + op.lotesPendentes)}
            detalhe={notaAndamento(op)}
            tom={op.lotesProcessando > 0 ? 'ok' : undefined}
          />
          <Indicador
            rotulo="Downloads agora"
            icone={<Download />}
            valor={`${numero(op.downloadsAtivos)} / ${numero(op.limites.downloadsSimultaneosGlobais)}`}
            detalhe="Em voo / teto, somando todos"
            tom={op.lotesProcessando > 0 && op.downloadsAtivos === 0 ? 'alerta' : undefined}
          />
          <Indicador
            rotulo="Concluídas"
            icone={<CheckCircle2 />}
            valor={numero(op.lotesConcluidos)}
            detalhe={op.lotesErro > 0 ? `${numero(op.lotesErro)} com erro` : 'ZIP ainda disponível'}
            tom={op.lotesErro > 0 ? 'alerta' : undefined}
          />
          <Indicador
            rotulo="Análises SPED"
            icone={<FileSearch />}
            valor={numero(op.analisesSped)}
          />
          <Indicador
            rotulo="Jobs simultâneos"
            icone={<Gauge />}
            valor={numero(op.limites.jobsSimultaneos)}
          />
          <IndicadorCaptura captura={captura} />
        </Indicadores>
      </section>
    </>
  );
}

/** Cartão-atalho para /admin/captura: saúde do laço e volume do dia. */
function IndicadorCaptura({ captura }: { captura: PanoramaCapturaAdmin | null | 'erro' }) {
  if (captura === null) {
    return <Indicador rotulo="Captura automática" icone={<Radar />} valor="—" para="/admin/captura" />;
  }
  if (captura === 'erro') {
    return (
      <Indicador
        rotulo="Captura automática"
        icone={<Radar />}
        valor="—"
        detalhe="Não foi possível consultar. Abrir panorama"
        para="/admin/captura"
      />
    );
  }
  const op = captura.operacao;
  const problema = !op.habilitado || !op.lacoLigado || op.empresasAtrasadas > 0;
  return (
    <Indicador
      rotulo="Captura automática"
      icone={<Radar />}
      valor={plural(captura.empresas.ativas, 'empresa', 'empresas')}
      tom={problema ? 'erro' : undefined}
      detalhe={
        problema
          ? !op.habilitado
            ? 'Desabilitada'
            : !op.lacoLigado
              ? 'Laço desligado'
              : `${plural(op.empresasAtrasadas, 'empresa atrasada', 'empresas atrasadas')}`
          : `${plural(captura.notas.ultimas24h, 'nota', 'notas')} em 24 h · ${reais(captura.receita.mes)} no mês`
      }
      para="/admin/captura"
    />
  );
}

/**
 * Situação das cobranças, no formato do painel do Asaas.
 *
 * Cada bloco mostra bruto e líquido: a diferença é a taxa do provedor, que é
 * quanto a operação custa de verdade — e some se só o bruto for exibido.
 */
function SituacaoCobrancas({
  financeiro,
}: {
  financeiro: FinanceiroAdmin | null;
}) {
  if (!financeiro) {
    return (
      <section className={css.secao}>
        <Esqueleto altura={20} largura={220} />
        <div className={css.esqueletos}>
          {[0, 1, 2, 3].map((i) => (
            <Esqueleto key={i} altura={136} />
          ))}
        </div>
      </section>
    );
  }

  if (!financeiro.disponivel) {
    return (
      <section className={css.secao}>
        <TituloSecao>Situação das cobranças</TituloSecao>
        <Aviso tom="alerta">
          Não foi possível consultar o provedor de pagamento: {financeiro.motivo}
        </Aviso>
      </section>
    );
  }

  const blocos = [
    {
      rotulo: 'Recebidas',
      dados: financeiro.situacoes.recebidas,
      icone: <CheckCircle2 />,
      tom: 'ok' as const,
    },
    {
      rotulo: 'Confirmadas',
      dados: financeiro.situacoes.confirmadas,
      icone: <BadgeCheck />,
      tom: undefined,
    },
    {
      rotulo: 'Aguardando pagamento',
      dados: financeiro.situacoes.aguardando,
      icone: <Clock />,
      tom: 'alerta' as const,
    },
    {
      rotulo: 'Vencidas',
      dados: financeiro.situacoes.vencidas,
      icone: <XCircle />,
      tom: 'erro' as const,
    },
  ];
  // Barra proporcional ao maior valor da linha.
  const maior = Math.max(1, ...blocos.map((b) => b.dados.bruto));

  return (
    <section className={css.secao}>
      <TituloSecao
        acao={
          <span className={css.saldo}>
            Saldo no Asaas: <strong>{reais(financeiro.saldoAsaas)}</strong>
          </span>
        }
      >
        Situação das cobranças
      </TituloSecao>

      <Indicadores>
        {blocos.map((b) => (
          <Indicador
            key={b.rotulo}
            rotulo={b.rotulo}
            icone={b.icone}
            valor={reais(b.dados.bruto)}
            tom={b.tom}
            para="/admin/financeiro"
            detalhe={
              <span className={css.cobranca}>
                <Progresso
                  valor={b.dados.bruto / maior}
                  rotulo={`${b.rotulo}: proporção do maior bloco`}
                  tom={b.tom === 'alerta' || b.tom === 'erro' ? b.tom : undefined}
                />
                <span>
                  {reais(b.dados.liquido)} líquido · {plural(b.dados.quantidade, 'cobrança')}
                </span>
              </span>
            }
          />
        ))}
      </Indicadores>
    </section>
  );
}
